import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, RefreshCw, UserX, KeyRound,
  Edit2, Filter, Users, CheckCircle, XCircle,
  AlertCircle, UserCheck
} from 'lucide-react';
import userService from '../../services/user.service';
import { useAuth } from '../../hooks/useAuth';
import { User, UserFilters } from '../../types';

// ── Role badge colors ─────────────────────────────────────────
const roleBadgeClass: Record<string, string> = {
  ADMIN:         'badge-purple',
  MD:            'badge-blue',
  PM:            'badge-blue',
  SITE_ENG:      'badge-yellow',
  ACCOUNTS:      'badge-green',
  PURCHASE:      'badge-green',
  HR:            'badge-green',
  ESTIMATOR:     'badge-gray',
  CUSTOMER:      'badge-gray',
  SUPPLIER:      'badge-gray',
  VENDOR:        'badge-gray',
  SUBCONTRACTOR: 'badge-yellow',
};

const UsersListPage = () => {
  const navigate      = useNavigate();
  const { hasRole }   = useAuth();
  const isAdmin       = hasRole('ADMIN');

  const [users,    setUsers]    = useState<User[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');

  // Filters
  const [search,    setSearch]    = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('');

  // Confirm dialog state
  const [confirmAction, setConfirmAction] = useState<{
    type: 'deactivate' | 'reset';
    user: User;
  } | null>(null);

  // ── Load users ──────────────────────────────────────────────
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const filters: UserFilters = {};
      if (roleFilter)                    filters.role_code = roleFilter;
      if (typeFilter)                    filters.user_type = typeFilter;
      if (activeFilter !== '')           filters.is_active = activeFilter === 'true';
      if (search)                        filters.search    = search;

      const data = await userService.getAllUsers(filters);
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [roleFilter, typeFilter, activeFilter, search]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  // ── Debounce search ─────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => loadUsers(), 400);
    return () => clearTimeout(timer);
  }, [search]);

  // ── Deactivate user ─────────────────────────────────────────
  const handleDeactivate = async (user: User) => {
    try {
      await userService.deactivateUser(user.user_id);
      setSuccess(`User '${user.username}' deactivated successfully.`);
      setConfirmAction(null);
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate user.');
      setConfirmAction(null);
    }
  };

  // ── Reset password ──────────────────────────────────────────
  const handleResetPassword = async (user: User) => {
    try {
      await userService.resetPassword(user.user_id);
      setSuccess(`Password reset for '${user.username}'. Default: Welcome@123`);
      setConfirmAction(null);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password.');
      setConfirmAction(null);
    }
  };

  // ── Summary stats ───────────────────────────────────────────
  const totalActive   = users.filter(u => u.is_active).length;
  const totalInactive = users.filter(u => !u.is_active).length;
  const totalLocked   = users.filter(u => u.locked_until && new Date(u.locked_until) > new Date()).length;

  return (
    <div className="page-container">

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">User Management</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage system users, roles and access
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => navigate('/users/create')}
            className="btn-primary"
          >
            <Plus size={16} />
            Create User
          </button>
        )}
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Users',  value: users.length,   icon: <Users size={20} />,       color: 'text-blue-600',  bg: 'bg-blue-50' },
          { label: 'Active',       value: totalActive,    icon: <UserCheck size={20} />,    color: 'text-green-600', bg: 'bg-green-50' },
          { label: 'Inactive',     value: totalInactive,  icon: <UserX size={20} />,        color: 'text-gray-500',  bg: 'bg-gray-50' },
          { label: 'Locked',       value: totalLocked,    icon: <AlertCircle size={20} />,  color: 'text-red-600',   bg: 'bg-red-50' },
        ].map(stat => (
          <div key={stat.label} className="card p-4 flex items-center gap-3">
            <div className={`p-2 rounded-lg ${stat.bg} ${stat.color}`}>
              {stat.icon}
            </div>
            <div>
              <p className="text-xl font-bold text-gray-800">{stat.value}</p>
              <p className="text-xs text-gray-500">{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Alerts */}
      {error   && <div className="alert-error mb-4"><XCircle size={16}/>{error}</div>}
      {success && <div className="alert-success mb-4"><CheckCircle size={16}/>{success}</div>}

      {/* Filters */}
      <div className="card mb-4">
        <div className="p-4 flex flex-wrap gap-3">

          {/* Search */}
          <div className="relative flex-1 min-w-48">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              className="form-input pl-9"
              placeholder="Search by name, username or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {/* Role filter */}
          <select
            className="form-select w-40"
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
          >
            <option value="">All Roles</option>
            {['ADMIN','MD','PM','SITE_ENG','ACCOUNTS','PURCHASE',
              'HR','ESTIMATOR','CUSTOMER','SUPPLIER','VENDOR','SUBCONTRACTOR']
              .map(r => <option key={r} value={r}>{r}</option>)}
          </select>

          {/* User type filter */}
          <select
            className="form-select w-44"
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            {['RBD_STAFF','SUPPLIER','VENDOR','TSP','CUSTOMER','SUBCONTRACTOR']
              .map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          {/* Active filter */}
          <select
            className="form-select w-36"
            value={activeFilter}
            onChange={e => setActiveFilter(e.target.value)}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>

          {/* Refresh */}
          <button onClick={loadUsers} className="btn-secondary px-3">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="table-container">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <Users size={40} className="mx-auto mb-3 opacity-40" />
              <p className="font-medium">No users found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Created</th>
                  {isAdmin && <th className="text-right">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {users.map(user => {
                  const isLocked = user.locked_until &&
                    new Date(user.locked_until) > new Date();

                  return (
                    <tr key={user.user_id}>
                      {/* User info */}
                      <td>
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center
                                          justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                            {user.full_name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{user.full_name}</p>
                            <p className="text-xs text-gray-400">@{user.username}</p>
                          </div>
                        </div>
                      </td>

                      {/* Role */}
                      <td>
                        <span className={`badge ${roleBadgeClass[user.role_code] || 'badge-gray'}`}>
                          {user.role_code}
                        </span>
                      </td>

                      {/* User type */}
                      <td>
                        <span className="text-gray-500 text-xs">{user.user_type}</span>
                      </td>

                      {/* Status */}
                      <td>
                        {!user.is_active ? (
                          <span className="badge badge-red">Inactive</span>
                        ) : isLocked ? (
                          <span className="badge badge-yellow">Locked</span>
                        ) : user.must_change_pwd ? (
                          <span className="badge badge-yellow">Pwd Change</span>
                        ) : (
                          <span className="badge badge-green">Active</span>
                        )}
                      </td>

                      {/* Last login */}
                      <td className="text-gray-500 text-xs">
                        {user.last_login_at
                          ? new Date(user.last_login_at).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })
                          : <span className="text-gray-300">Never</span>
                        }
                      </td>

                      {/* Created */}
                      <td className="text-gray-500 text-xs">
                        {new Date(user.created_at).toLocaleDateString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric'
                        })}
                      </td>

                      {/* Actions */}
                      {isAdmin && (
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={() => navigate(`/users/${user.user_id}/edit`)}
                              className="p-1.5 text-gray-400 hover:text-blue-600
                                         hover:bg-blue-50 rounded-lg transition-colors"
                              title="Edit user"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => setConfirmAction({ type: 'reset', user })}
                              className="p-1.5 text-gray-400 hover:text-yellow-600
                                         hover:bg-yellow-50 rounded-lg transition-colors"
                              title="Reset password"
                            >
                              <KeyRound size={14} />
                            </button>
                            {user.is_active && (
                              <button
                                onClick={() => setConfirmAction({ type: 'deactivate', user })}
                                className="p-1.5 text-gray-400 hover:text-red-600
                                           hover:bg-red-50 rounded-lg transition-colors"
                                title="Deactivate user"
                              >
                                <UserX size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Confirm Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center
                        justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4 ${
              confirmAction.type === 'deactivate' ? 'bg-red-100' : 'bg-yellow-100'
            }`}>
              {confirmAction.type === 'deactivate'
                ? <UserX size={24} className="text-red-600" />
                : <KeyRound size={24} className="text-yellow-600" />
              }
            </div>
            <h3 className="text-center font-semibold text-gray-800 mb-2">
              {confirmAction.type === 'deactivate' ? 'Deactivate User' : 'Reset Password'}
            </h3>
            <p className="text-center text-sm text-gray-500 mb-6">
              {confirmAction.type === 'deactivate'
                ? `Are you sure you want to deactivate '${confirmAction.user.full_name}'? All active sessions will be revoked.`
                : `Reset password for '${confirmAction.user.full_name}'? They will need to change it on next login.`
              }
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmAction(null)}
                className="btn-secondary flex-1 justify-center"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmAction.type === 'deactivate'
                  ? handleDeactivate(confirmAction.user)
                  : handleResetPassword(confirmAction.user)
                }
                className={confirmAction.type === 'deactivate' ? 'btn-danger flex-1 justify-center' : 'btn-primary flex-1 justify-center'}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersListPage;